import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NewTripRequest } from '../../types';
import { TripPlannerAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import WizardLayout from './WizardLayout';
import StepDestination from './StepDestination';
import StepDatesInterests from './StepDatesInterests';
import StepInterestsPace from './StepInterestsPace';
import StepLocations from './StepLocations';
import StepLodging from './StepLodging';
import StepBudgetTransport from './StepBudgetTransport';
import StepNotes from './StepNotes';
import TripGenerationLoader from './TripGenerationLoader';
import AuthModal from '../AuthModal';
import { TripCreationTimeoutError } from '../../services/errors/TripCreationTimeoutError';

// Initial empty state for the form
const initialFormData: NewTripRequest = {
  destination: '',
  startDate: '',
  endDate: '',
  travelers: {
    adults: 1,
    children: 0,
    childAges: []
  },
  startingLocation: '',
  endingLocation: '',
  mustSeePlaces: [],
  interests: [],
  budget: undefined,
  accommodationType: undefined,
  transportationPreference: undefined,
  pace: undefined,
  lodging: {
    type: undefined,
    allTripSame: true,
    dailyPreferences: []
  },
  specialRequirements: [],
  additionalNotes: ''
};

const CreateTripWizard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<NewTripRequest>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const [dailyLodging, setDailyLodging] = useState<{[key: string]: {status: 'has' | 'none' | 'need', comment: string}}>({});
  const [lodgingInput, setLodgingInput] = useState('');

  const totalSteps = 7;

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
      window.scrollTo(0, 0);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      window.scrollTo(0, 0);
    }
  };

  const getTripDays = () => {
    if (!formData.startDate || !formData.endDate) return [];
    
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    const days = [];
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      days.push(new Date(d).toISOString().split('T')[0]);
    }
    
    return days;
  };

  const handleSubmit = async () => {
    // Check if user is authenticated
    if (!user) {
      setIsAuthModalOpen(true);
      return;
    }

    setIsSubmitting(true);
    try {
      // Build lodging notes from daily lodging data
      let lodgingNotes = '';
      if (lodgingInput.trim()) {
        lodgingNotes += `General lodging preferences: ${lodgingInput.trim()}\n\n`;
      }
      
      // Add daily lodging information
      const tripDays = getTripDays();
      const dailyLodgingNotes = tripDays
        .map(date => {
          const lodging = dailyLodging[date];
          if (!lodging) return null;
          
          const dayNumber = tripDays.indexOf(date) + 1;
          const formattedDate = new Date(date).toLocaleDateString('en-US', { 
            weekday: 'short', 
            month: 'short', 
            day: 'numeric' 
          });
          
          if (lodging.status === 'has' && lodging.comment.trim()) {
            return `Day ${dayNumber} (${formattedDate}): Already have lodging - ${lodging.comment.trim()}`;
          } else if (lodging.status === 'none') {
            return `Day ${dayNumber} (${formattedDate}): No lodging needed`;
          } else if (lodging.status === 'need') {
            return `Day ${dayNumber} (${formattedDate}): Need lodging recommendation`;
          }
          return null;
        })
        .filter(Boolean)
        .join('\n');
      
      if (dailyLodgingNotes) {
        lodgingNotes += `Daily lodging status:\n${dailyLodgingNotes}`;
      }
      
      // Include lodging data in the form data
      const submitData = {
        ...formData,
        additionalNotes: lodgingNotes.trim() 
          ? `${formData.additionalNotes ? formData.additionalNotes + '\n\n' : ''}${lodgingNotes.trim()}`
          : formData.additionalNotes
      };

      console.log('Submitting trip data:', submitData);
      const response = await TripPlannerAPI.createAITrip(submitData);
      if (response.success && response.tripId) {
        navigate(`/trips/${response.tripId}`);
      } else {
        // Handle error (maybe show a toast)
        console.error('Failed to create trip:', response.message);
        alert('Failed to create trip: ' + response.message);
      }
    } catch (error: any) {
      console.error('Error submitting form:', error);
      if (error instanceof TripCreationTimeoutError) {
        navigate('/trips', {
          replace: true,
          state: {
            infoMessage:
              "Trip creation is taking longer than expected. It may still complete in the background—please check it in 'My Trips'.",
          },
        });
        return;
      }

      alert('Error creating trip: ' + (error?.message || 'Unknown error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine if the current step is valid to proceed
  const isStepValid = () => {
    switch (step) {
      case 1: // Destination & Travelers
        return !!formData.destination.trim() && formData.travelers.adults >= 1;
      case 2: // Dates
        return !!formData.startDate && !!formData.endDate;
      case 3: // Locations
        // Optional
        return true;
      case 4: // Interests & Pace
        // Optional
        return true;
      case 5: // Lodging
        // Optional
        return true;
      case 6: // Budget & Transport
        // Optional
        return true;
      case 7: // Notes
        // Optional
        return true;
      default:
        return false;
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 1: return "Where are you going?";
      case 2: return "When are you traveling?";
      case 3: return "Route & Stops";
      case 4: return "Travel Style & Interests";
      case 5: return "Where to stay?";
      case 6: return "Budget & Travel";
      case 7: return "Final Details";
      default: return "";
    }
  };

  const getStepSubtitle = () => {
    switch (step) {
      case 1: return "Let's start with your dream destination and who's coming along.";
      case 2: return "Pick your travel dates.";
      case 3: return "Planning a road trip? Add stops along the way.";
      case 4: return "Tell us what you love to do so we can tailor the trip to you.";
      case 5: return "Help us find the perfect place for you to rest.";
      case 6: return "Set your budget and how you prefer to get around.";
      case 7: return "Any special requests or notes for the AI planner?";
      default: return "";
    }
  };

  const getNextLabel = () => {
    if (step === totalSteps) return 'Create Trip';

    let hasData = false;
    switch (step) {
      case 3: // Locations
        hasData = !!formData.startingLocation || !!formData.endingLocation || formData.mustSeePlaces.length > 0;
        break;
      case 4: // Interests & Pace
        hasData = !!formData.pace || formData.interests.length > 0;
        break;
      case 5: // Lodging
        hasData = !!lodgingInput.trim() || Object.keys(dailyLodging).length > 0 || !!formData.accommodationType;
        break;
      case 6: // Budget & Transport
        hasData = !!formData.budget || !!formData.transportationPreference;
        break;
      case 7: // Notes
        hasData = !!formData.additionalNotes || formData.specialRequirements.length > 0;
        break;
      default:
        hasData = true; // Required steps
    }
    
    // Steps 3-7 are optional. If no data entered, show "Skip"
    if (step >= 3 && !hasData) {
      return 'Skip';
    }
    
    return 'Next';
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return <StepDestination formData={formData} setFormData={setFormData} onNext={handleNext} />;
      case 2:
        return <StepDatesInterests formData={formData} setFormData={setFormData} />;
      case 3:
        return <StepLocations formData={formData} setFormData={setFormData} />;
      case 4:
        return <StepInterestsPace formData={formData} setFormData={setFormData} />;
      case 5:
        return (
          <StepLodging 
            formData={formData} 
            setFormData={setFormData}
            dailyLodging={dailyLodging}
            setDailyLodging={setDailyLodging}
            lodgingInput={lodgingInput}
            setLodgingInput={setLodgingInput}
          />
        );
      case 6:
        return <StepBudgetTransport formData={formData} setFormData={setFormData} />;
      case 7:
        return <StepNotes formData={formData} setFormData={setFormData} />;
      default:
        return null;
    }
  };

  return (
    <>
      {isSubmitting && <TripGenerationLoader />}
      
      <WizardLayout
        title={getStepTitle()}
        subtitle={getStepSubtitle()}
        currentStep={step}
        totalSteps={totalSteps}
        onNext={handleNext}
        onBack={handleBack}
        canNext={isStepValid()}
        isSubmitting={isSubmitting}
        nextLabel={getNextLabel()}
      >
        {renderStep()}
      </WizardLayout>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        mode="signin"
      />
    </>
  );
};

export default CreateTripWizard;
